// @ts-nocheck
import { Link } from "react-router-dom";
import { INotificationInvite, Notifications } from "@/integration/application/interfaces/notification.interface";
import { isScheduleInvite, isTeamInvite } from "./types";
import { TeamNotificationUserStatus } from "@integration/team/interfaces/team.interface.ts";

/**
 * Renders content for a team invitation notification
 */
export const renderTeamInvite = (
  notification: Notifications, 
  userAuthId: string | undefined,
  compact = false
): React.ReactNode => {
  if (typeof notification.body !== 'object') {
    return renderStringArrayBody(notification, compact);
  }
  
  const body = notification.body as INotificationInvite;
  const teamName = body.team?.name || 'Desconhecido';
  const role = body.role || 'Membro';
  const formattedDate = formatDate(body.created_at);
  
  // Pending invitation
  if (body.status === 'PENDING') {
    return (
      <span>
        Você foi convidado para se juntar a Equipe <span className="text-red-400 font-semibold">{teamName}</span> com função de <span className="text-blue-400 font-semibold">{role}</span>. 
        {!compact && <span className="block mt-1 text-gray-400 text-sm">Aceite ou recuse abaixo.</span>}
      </span>
    );
  }
  
  // Accepted invitation
  if (body.status === TeamNotificationUserStatus.ACCEPTED && (body.userWhoInvited.id !== userAuthId || body.user_id !== userAuthId)) {
    return (
      <span>
        Você aceitou o convite para participar do time <span className="text-red-400 font-semibold">{teamName}</span> com função de <span className="text-blue-400 font-semibold">{role}</span>.
        {formattedDate && <span className="block mt-1 text-gray-400 text-xs">{formattedDate}</span>}
      </span>
    );
  }
  
  // Declined invitation
  if (body.status === 'DECLINED' || notification.type === 'REFUSED') {
    // If the user is the one who declined the invitation
    if (body.user?.id === userAuthId) {
      return (
        <span>
          Você recusou o convite para participar do time <span className="text-red-400 font-semibold">{teamName}</span>.
          {formattedDate && <span className="block mt-1 text-gray-400 text-xs">{formattedDate}</span>}
        </span>
      );
    } 
    // If the user is the one who sent the invitation
    else if (body.userWhoInvited?.id === userAuthId) {
      const invitedUserNick = body.user?.nick || 'Um usuário';
      const invitedUserLink = <Link to={`/pro/${invitedUserNick}`} className="text-red-400 font-bold">@{invitedUserNick}</Link>;

      return (
        <span>
          {invitedUserLink} recusou seu convite para participar do time <span className="text-red-400 font-semibold">{teamName}</span>.
          {formattedDate && <span className="block mt-1 text-gray-400 text-xs">{formattedDate}</span>}
        </span>
      );
    }
  }
  
  return null;
};

/**
 * Renders content for a schedule invitation notification
 */
export const renderScheduleInvite = (
  notification: Notifications, 
  userAuthId: string | undefined,
  compact = false
): React.ReactNode => {

  if (typeof notification.body !== 'object') {
    return renderStringArrayBody(notification, compact);
  }
  
  const body = notification.body as INotificationInvite;
  const eventName = body.schedule.title || 'Desconhecido';
  
  // Pending invitation
  if (body.status === 'PENDING') {
    return (
      <span>
        Você foi convidado para o evento <span className="text-red-400 font-semibold">{eventName}</span>. 
        {!compact && <span className="block mt-1 text-gray-400 text-sm">Aceite ou recuse abaixo.</span>}
      </span>
    );
  } else if (body.status === TeamNotificationUserStatus.ACCEPTED || body?.is_confirmed) {
    return (
      <span>
        Você aceitou o convite para o evento <span className="text-red-400 font-semibold">{eventName}</span>.
        {!compact && <span className="block mt-1 text-gray-400 text-sm">Convite aceito</span>}
      </span>
      )

  }

  return null;
};

/**
 * Renders content for a team join notification
 */
export const renderTeamJoin = (
  notification: Notifications, 
  userAuthId: string | undefined,
  compact = false
): React.ReactNode => {
  if (typeof notification.body !== 'object') {
    return renderStringArrayBody(notification, compact);
  }
  
  const body = notification.body as INotificationInvite;
  const teamName = body.team?.name || 'Desconhecido';
  const role = body.role || 'Membro';
  const formattedDate = formatDate(body.created_at);
  
  if (body.status === 'ACCEPTED') {
    // If the current user is the one who joined the team
    if (body.user?.id === userAuthId) {
      return (
        <span>
          Você foi adicionado ao time <span className="text-red-400 font-semibold">{teamName}</span> com função de <span className="text-blue-400 font-semibold">{role}</span>.
          {formattedDate && <span className="block mt-1 text-gray-400 text-xs">{formattedDate}</span>}
        </span>
      );
    } 
    // If the current user is the one who invited
    else if (body.userWhoInvited?.id === userAuthId) {
      const joinedUserNick = body.user?.nick || 'Um usuário';
      const joinedUserLink = <Link to={`/pro/${joinedUserNick}`} className="text-red-400 font-bold">@{joinedUserNick}</Link>;

      return (
        <span>
          {joinedUserLink} aceitou seu convite e foi adicionado ao time <span className="text-red-400 font-semibold">{teamName}</span> com função de <span className="text-blue-400 font-semibold">{role}</span>.
          {formattedDate && <span className="block mt-1 text-gray-400 text-xs">{formattedDate}</span>}
        </span>
      );
    }
    // For other team members
    else {
      const joinedUserNick = body.user?.nick || 'Um usuário';
      const joinedUserLink = <Link to={`/pro/${joinedUserNick}`} className="text-red-400 font-bold">@{joinedUserNick}</Link>;
      const inviterNick = body.userWhoInvited?.nick || 'Um usuário';
      const inviterLink = <Link to={`/pro/${inviterNick}`} className="text-red-400 font-bold">@{inviterNick}</Link>;

      return (
        <span>
          {joinedUserLink} foi adicionado ao time <span className="text-red-400 font-semibold">{teamName}</span> por {inviterLink} com função de <span className="text-blue-400 font-semibold">{role}</span>.
          {formattedDate && <span className="block mt-1 text-gray-400 text-xs">{formattedDate}</span>}
        </span>
      );
    }
  }
  
  return null;
};

/**
 * Renders content for a notification with string array body
 */
export const renderStringArrayBody = (
  notification: Notifications, 
  compact = false
): React.ReactNode => {
  if (!Array.isArray(notification.body)) {
    return notification.body;
  }
  
  // For team/schedule invitations with string array body
  if (isTeamInvite(notification) || isScheduleInvite(notification)) {
    const message = notification.body.join(' ');
    return (
      <span>
        {message}
        {!compact && <span className="block mt-1 text-gray-400 text-sm">Aceite ou recuse abaixo.</span>}
      </span>
    );
  }
  
  // Default handling for string array body
  return notification.body.join(' ');
};

/**
 * Renders default content for a notification
 */
export const renderDefaultContent = (
  notification: Notifications, 
  compact = false
): React.ReactNode => {
  // For string body
  if (typeof notification.body !== 'object' && !Array.isArray(notification.body)) {
    return notification.body;
  }
  
  // Default fallback for object body
  if (typeof notification.body === 'object' && !Array.isArray(notification.body)) {
    return (
      <span>
        <span className="text-gray-400">Notificação: {notification.type}</span>
        {!compact && (
          <span className="block mt-1 text-xs font-mono bg-gray-800/50 p-2 rounded border border-gray-700/50 overflow-x-auto max-w-full">
            {JSON.stringify(notification.body, null, compact ? 0 : 2)}
          </span>
        )}
      </span>
    );
  }
  
  return null;
};

/**
 * Format a date for display
 */
export const formatDate = (date: Date | string | undefined): string => {
  if (!date) return '';
  
  return new Date(date).toLocaleDateString('pt-BR', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric' 
  }) + ' - ' + new Date(date).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  });
};